import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Trophy, Medal } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { GlobalLeaderboard } from '@/components/GlobalLeaderboard';
import { DinoAvatar } from '@/components/DinoAvatar';
import { useToast } from '@/hooks/use-toast';
import { isFeatureEnabled } from '@/lib/featureFlags';

interface ProfileData {
  id: string;
  username: string;
  name: string;
  bio?: string;
  image?: string;
}

interface UserStats {
  totalBooks: number;
  averageRating: number;
  rankedBooks: number;
}

export default function LeaderboardPage() {
  const { username } = useParams<{ username: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [stats, setStats] = useState<UserStats>({
    totalBooks: 0,
    averageRating: 0,
    rankedBooks: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Feature flag check
  const pairwiseEnabled = isFeatureEnabled('PAIRWISE_RANKING');

  useEffect(() => {
    if (!pairwiseEnabled) {
      navigate('/');
      return;
    }
    
    if (username) {
      loadLeaderboardData();
    }
  }, [username, user, pairwiseEnabled]);

  const loadLeaderboardData = async () => {
    if (!username) return;
    
    try {
      setLoading(true);
      setError(null);

      // Load profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', username)
        .maybeSingle();

      if (profileError) {
        console.error('Profile load error:', profileError);
        setError('Failed to load profile');
        return;
      }

      if (!profileData) {
        setProfile(null);
        return;
      }

      setProfile(profileData);

      // Load stats
      const [booksResult, scoresResult] = await Promise.all([
        supabase
          .from('user_books')
          .select('rating_star')
          .eq('user_id', profileData.id)
          .eq('privacy', 'public'),
        supabase
          .from('user_tier_scores')
          .select('book_id')
          .eq('user_id', profileData.id)
      ]);

      const totalBooks = booksResult.data?.length || 0;
      const ratingsSum = booksResult.data?.filter(b => b.rating_star).reduce((sum, b) => sum + (b.rating_star || 0), 0) || 0;
      const ratingsCount = booksResult.data?.filter(b => b.rating_star).length || 0;
      const averageRating = ratingsCount > 0 ? ratingsSum / ratingsCount : 0;
      const rankedBooks = scoresResult.data?.length || 0;

      setStats({
        totalBooks,
        averageRating,
        rankedBooks
      });

    } catch (error) {
      console.error('Error loading leaderboard:', error);
      setError('An unexpected error occurred');
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load leaderboard data",
      });
    } finally {
      setLoading(false);
    }
  };

  if (!pairwiseEnabled) {
    return null; // Will redirect in useEffect
  }

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            <span className="ml-3 text-lg">Loading leaderboard...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">Leaderboard Unavailable</h2>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={loadLeaderboardData} variant="outline">
                  Try Again
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)}
              className="mb-4"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Card className="card-elevated">
              <CardContent className="p-8 text-center">
                <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h2 className="text-xl font-semibold mb-2">User Not Found</h2>
                <p className="text-muted-foreground mb-4">
                  The user @{username} doesn't exist or their leaderboard is private.
                </p>
                <Button onClick={() => navigate('/search?tab=people')} variant="outline">
                  Find People
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  const isOwnProfile = user?.id === profile.id;

  return (
    <div className="min-h-[100dvh] bg-background w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-[var(--header-height)] z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              onClick={() => navigate(`/u/${username}`)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Profile
            </Button>
            
            <div className="flex items-center gap-3">
              <Crown className="w-5 h-5 text-primary" />
              <h1 className="font-heading font-bold text-lg">All-Time Leaderboard</h1>
            </div>
            
            <div className="w-24" /> {/* Spacer */}
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Profile Summary */}
        <Card className="card-elevated mb-8">
          <CardHeader>
            <div className="flex items-center gap-4">
              <DinoAvatar 
                src={profile.image}
                name={profile.name}
                size="lg"
                className="ring-2 ring-primary/20"
              />
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  {profile.name}
                  <Badge variant="secondary">@{profile.username}</Badge>
                </CardTitle>
                <p className="text-muted-foreground mt-1">
                  Personal book rankings based on preferences
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-2xl font-bold text-primary">{stats.totalBooks}</div>
                <div className="text-sm text-muted-foreground">Total Books</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">{stats.averageRating.toFixed(1)}</div>
                <div className="text-sm text-muted-foreground">Avg Rating</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-primary">{stats.rankedBooks}</div>
                <div className="text-sm text-muted-foreground">Ranked Books</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leaderboard */}
        <GlobalLeaderboard 
          userId={profile.id} 
          isOwnProfile={isOwnProfile}
        />
      </main>
    </div>
  );
}